// src/composables/useShare.js
// 导入 Vue3 的 ref（用于响应式配置分享参数）
import { ref } from 'vue'

// 导出组合式函数（核心：任何组件导入这个函数，就能使用里面的分享方法）
export function useShare() {
  // 1. 分享基础配置（响应式，可被外部修改）
  // 默认值：标题取当前页面标题，链接取当前页面URL，描述为空（可自定义）
  const shareConfig = ref({
    title: document.title,    // 分享标题（默认当前页面标题）
    url: window.location.href,// 分享链接（默认当前页面URL）
    desc: ''                  // 分享描述（可选，LinkedIn 可用）
  })

  // 2. 复制链接工具函数（兼容所有浏览器，解决 Ins/抖音 无法直接分享的问题）
  // 参数1：text = 要复制的链接；参数2：lang = 当前语言（zh-CN/en，用于多语言提示）
  const copyToClipboard = (text, lang = 'zh-CN') => {
    try {
      // 现代浏览器（Chrome、Edge、Safari）：用原生剪贴板API复制
      navigator.clipboard.writeText(text).then(() => {
        // 提示语：根据当前语言切换（和你原有提示一致）
        alert(lang === 'zh-CN' ? '分享链接已复制！' : 'Share link copied!')
      })
    } catch (err) {
      // 降级处理（兼容旧浏览器、特殊环境）：创建临时输入框复制
      const input = document.createElement('input') // 新建输入框
      input.value = text                            // 输入框赋值为要复制的链接
      document.body.appendChild(input)              // 把输入框添加到页面（临时）
      input.select()                                // 选中输入框内容
      document.execCommand('copy')                  // 执行复制操作
      document.body.removeChild(input)              // 删除临时输入框
      // 同样提示复制成功
      alert(lang === 'zh-CN' ? '分享链接已复制！' : 'Share link copied!')
    }
  }

  // 3. 核心分享函数（调用这个函数，就能实现对应平台的分享）
  // 参数1：platform = 分享平台（tiktok/x/facebook/ins/linkedin，和你图标对应）
  // 参数2：lang = 当前语言（必传，用于提示语切换）
  // 参数3：customConfig = 自定义分享配置（可选，比如在详情页修改分享标题/链接）
  const shareTo = (platform, lang = 'zh-CN', customConfig = {}) => {
    // 合并配置：默认配置 + 外部自定义配置（外部传了就用外部的，没传就用默认的）
    const { title, url } = { ...shareConfig.value, ...customConfig }
    let shareUrl = '' // 用于存储各平台的分享链接（弹窗式分享用）

    // 根据不同平台，执行不同的分享逻辑（和你原有逻辑一致，只是抽离出来）
    switch (platform) {
      // 抖音：无网页分享接口，复制链接
      case 'tiktok':
        copyToClipboard(url, lang)
        break
      // X（原Twitter）：打开官方分享弹窗
      case 'x':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`
        window.open(shareUrl, '_blank', 'width=600,height=400') // 打开新窗口，控制大小
        break
      // Facebook：打开官方分享弹窗
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
        window.open(shareUrl, '_blank', 'width=600,height=400')
        break
      // Ins：无网页分享接口，复制链接
      case 'ins':
        copyToClipboard(url, lang)
        break
      // LinkedIn：打开官方分享弹窗
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
        window.open(shareUrl, '_blank', 'width=600,height=400')
        break
      // 异常情况：不支持的平台
      default:
        alert(lang === 'zh-CN' ? '暂不支持该平台分享' : 'Sharing not supported for this platform')
    }
  }

  // 4. 外部修改分享配置的函数（可选，比如在详情页，需要分享当前文章的标题/链接）
  // 参数：newConfig = 自定义配置（比如 { title: '文章标题', url: '文章链接' }）
  const setShareConfig = (newConfig) => {
    // 合并原有配置和新配置，不覆盖原有未修改的参数
    shareConfig.value = { ...shareConfig.value, ...newConfig }
  }

  // 5. 导出函数和配置（供外部组件导入使用）
  return {
    shareTo,          // 核心分享函数（必导，外部调用这个函数实现分享）
    setShareConfig,   // 自定义分享配置（可选导，需要修改分享内容时用）
    copyToClipboard   // 单独复制链接的函数（可选导，单独用复制功能时用）
  }
}